// Centralized HTTP client for the Operandi backend.
// Relative paths (/api, /assets) resolve to the backend via the CRA dev proxy
// (package.json "proxy") in dev, or same-origin in production. Override with
// REACT_APP_API_BASE when frontend and backend are served from different origins.

const API_BASE = process.env.REACT_APP_API_BASE || "";
const TOKEN_KEY = "operandi_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

// Build an absolute URL for an asset path returned by the API (e.g. "/assets/...").
export const assetUrl = (path) => {
  if (!path) return path;
  if (/^https?:\/\//.test(path)) return path;
  return `${API_BASE}${path}`;
};

class ApiError extends Error {
  constructor(message, status) {
    super(message);
    this.status = status;
  }
}

const buildError = async (res) => {
  let detail = res.statusText;
  try {
    const body = await res.json();
    detail = body.detail || detail;
  } catch (_) {
    /* non-JSON error body */
  }
  return new ApiError(detail, res.status);
};

export async function apiFetch(path, { method = "GET", body, form, headers = {}, auth = true } = {}) {
  const opts = { method, headers: { ...headers } };

  if (auth) {
    const token = getToken();
    if (token) opts.headers.Authorization = `Bearer ${token}`;
  }

  // GET/HEAD cannot carry a body; guard so a missing method can't break fetch.
  const canHaveBody = method !== "GET" && method !== "HEAD";

  if (canHaveBody && form) {
    // URL-encoded form (used by the OAuth2 login endpoint).
    opts.headers["Content-Type"] = "application/x-www-form-urlencoded";
    opts.body = new URLSearchParams(form).toString();
  } else if (canHaveBody && body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }

  const res = await fetch(`${API_BASE}${path}`, opts);

  if (res.status === 401) {
    clearToken();
    // Avoid redirect loops on the login page itself.
    if (!window.location.pathname.startsWith("/login")) {
      window.location.assign("/login");
    }
    throw await buildError(res);
  }

  if (!res.ok) throw await buildError(res);
  if (res.status === 204) return null;
  return res.json();
}

export { ApiError };
